import { useEffect } from 'react';
import { useParams } from 'react-router';
import { match, P } from 'ts-pattern';
import LinkButton from '~/components/button/LinkButton';
import Page from '~/components/layout/page/Page';
import {
  NetworkError,
  ResponseError,
  StoreNotFoundError,
} from '~/utils/errors';
import { logError } from '~/utils/observability';
import { useRouteError } from '~/utils/routing';

const getHomePath = (params: ReturnType<typeof useParams>) =>
  match(params)
    .with(
      { idFromPlatform: P.string, store: P.string },
      ({ idFromPlatform, store }) => `/${store}/order/${idFromPlatform}`,
    )
    .with({ store: P.string }, ({ store }) => `/${store}`)
    .otherwise(() => '/');

function HomeLink() {
  const params = useParams();
  const path = getHomePath(params);

  return (
    <LinkButton
      className="mt-6 w-full sm:w-1/2"
      to={path}
      reloadDocument // force a reload to hopefully get any app updates
    >
      Home
    </LinkButton>
  );
}

function ErrorLayout({ children }: { children: React.ReactNode }) {
  return (
    <main className="width-full min-h-full bg-surface px-6 py-24 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-2xl">{children}</div>
    </main>
  );
}

function DefaultError({ error }: { error: Error }) {
  useEffect(() => {
    logError(error);
  }, [error]);

  return (
    <ErrorLayout>
      <Page.Root title="Something went wrong...">
        <p>Please go home and try again.</p>
        <HomeLink />
      </Page.Root>
    </ErrorLayout>
  );
}

function StoreNotFound({ error }: { error: StoreNotFoundError }) {
  useEffect(() => {
    logError(error);
  }, [error]);

  return (
    <ErrorLayout>
      <Page.Root title="Page not found">
        <p>We are unable to find the page you are looking for</p>
        <HomeLink />
      </Page.Root>
    </ErrorLayout>
  );
}

function NetworkErrorDisplay() {
  return (
    <ErrorLayout>
      <Page.Root title="Connection lost">
        <p>Please check your connection and try again.</p>
        <LinkButton className="w-1/2" to="#" reloadDocument>
          Reload
        </LinkButton>
      </Page.Root>
    </ErrorLayout>
  );
}

function ResponseErrorDisplay({ error }: { error: ResponseError }) {
  useEffect(() => {
    logError(error);
  }, [error]);

  return (
    <ErrorLayout>
      <Page.Root title="Something went wrong...">
        <p>{error.message}</p>
        <HomeLink />
      </Page.Root>
    </ErrorLayout>
  );
}

export default function ErrorPage() {
  const error = useRouteError();

  // Remove the splash screen
  useEffect(() => {
    document.body.dataset.ready = 'ready';
  }, []);

  return match(error)
    .with(P.instanceOf(StoreNotFoundError), (e) => <StoreNotFound error={e} />)
    .with(P.instanceOf(NetworkError), () => <NetworkErrorDisplay />)
    .with(P.instanceOf(ResponseError), (e) => (
      <ResponseErrorDisplay error={e} />
    ))
    .otherwise((e) => <DefaultError error={e} />);
}
